import type { AgentEvent, AgentPhase, StatusEvent } from "@skaile/workspaces/types";

/**
 * Mirrors the platform gateway's turn-state reduction over the runner's
 * outbound events so a (re)connect can replay the status the gateway would
 * hold had the connection never dropped. A non-idle `status` opens the turn
 * with that phase; `status idle`, `finished` and `error` close it.
 *
 * Not derived from the driver-swap gate on purpose: flow-driven turns never
 * open it and a reply opens it before any phase is emitted, so the gate
 * cannot answer "which phase" at either edge; the event stream always can.
 */
export class TurnStatusTracker {
  private phase: Exclude<AgentPhase, "idle"> | null = null;

  /** Feed every outbound event — connected or not — before it is sent. */
  observe(event: AgentEvent): void {
    switch (event.type) {
      case "status":
        this.phase = event.phase === "idle" ? null : event.phase;
        return;
      case "finished":
      case "error":
        this.phase = null;
        return;
      default:
        return;
    }
  }

  /** The `status` event that reproduces the current turn state on a fresh connect. */
  snapshot(): StatusEvent {
    return { type: "status", phase: this.phase ?? "idle" };
  }
}
